import type { ReactNode } from "react";
import type { Visibility } from "@hector/shared-types/resource-sharing";
import { Popover, PopoverContent, PopoverAnchor } from "@/components/ui/popover";
import { useCabinetId } from "@/hooks/useCabinetMemberMe";
import { useCabinetMembers } from "@/hooks/cabinet/useCabinetMembers";
import {
  useAddConversationShare,
  useConversationShares,
  useRemoveConversationShare,
  useUpdateConversationVisibility,
} from "@/hooks/useConversationShares";
import { ShareControl } from "@/components/sharing/ShareControl";
import { useShareController } from "@/components/sharing/useShareController";
import { guardNestedPopoverInteractOutside, markShareAnchor } from "@/components/sharing/nestedPopoverGuard";
import { SHARE_POPOVER_CONTENT_CLASSNAME } from "@/components/sharing/sharePopoverLayout";

interface ShareConversationPopoverProps {
  conversationId: string;
  /** Propriétaire du fil (`ownerId`) — exclu de la liste des destinataires. */
  ownerId: string;
  /** Visibilité courante du fil (`private` par défaut si non enrichie). */
  currentVisibility: Visibility;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Élément d'ANCRAGE (le bouton ⋮ de la conversation) — le panneau se positionne
   * à côté. Ouverture pilotée par `open`/`onOpenChange` (item « Partager » du menu),
   * d'où `PopoverAnchor` et non `PopoverTrigger`.
   */
  children: ReactNode;
}

/**
 * Panneau « Partager la conversation » — owner-only (le point d'entrée est déjà
 * masqué pour les non-owners, et le backend re-garde). Popover ancré au clic
 * (Radix, sans overlay/blur) autour de `ShareControl`, pilotée par
 * `useShareController` branché sur les hooks `useConversationShares`/
 * `useAddConversationShare`/`useRemoveConversationShare`/
 * `useUpdateConversationVisibility` (endpoints `/conversations/:id/shares` +
 * `/visibility`).
 *
 * shadcn `<Popover>` (Radix) — jamais de portal maison (crashs `insertBefore`, cf.
 * CLAUDE.md de l'app). Remplace l'ancien `<Dialog>` centré (blur overlay) — décision
 * produit 2026-07-07.
 */
export function ShareConversationPopover({
  conversationId,
  ownerId,
  currentVisibility,
  open,
  onOpenChange,
  children,
}: ShareConversationPopoverProps) {
  const cabinetId = useCabinetId();
  const { data: members, isLoading: membersLoading } = useCabinetMembers(cabinetId);
  const { data: shares, isLoading: sharesLoading } = useConversationShares(
    open ? conversationId : null,
  );

  const addShare = useAddConversationShare(conversationId);
  const removeShare = useRemoveConversationShare(conversationId);
  const updateVisibility = useUpdateConversationVisibility(conversationId);

  const ctrl = useShareController({
    currentVisibility,
    open,
    shares,
    addShare: (a) => addShare.mutateAsync(a),
    removeShare: (u) => removeShare.mutateAsync(u),
    updateVisibility: (v) => updateVisibility.mutateAsync(v),
    updateVisibilityPending: updateVisibility.isPending,
  });

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverAnchor asChild>{markShareAnchor(children)}</PopoverAnchor>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={6}
        collisionPadding={16}
        className={SHARE_POPOVER_CONTENT_CLASSNAME}
        aria-label="Partager la conversation"
        onInteractOutside={guardNestedPopoverInteractOutside}
        // Le contenu (recherche, lignes de partage) n'a aucun stopPropagation
        // interne : sans celui-ci, un clic bubble à travers l'arbre REACT
        // (`PopoverContent` est porté hors du DOM de la ligne de conversation
        // mais reste un descendant React) jusqu'au onClick de la ligne, qui
        // navigue vers la conversation au lieu de rester dans le panneau
        // (bug constaté en test live, 2026-07-07).
        onClick={(e) => e.stopPropagation()}
      >
        <ShareControl
          visibility={ctrl.visibility}
          onVisibilityChange={ctrl.onVisibilityChange}
          shares={ctrl.shares}
          onAddPerson={ctrl.onAddPerson}
          onChangePersonRole={ctrl.onChangePersonRole}
          onRemovePerson={ctrl.onRemovePerson}
          members={members}
          ownerId={ownerId}
          isLoading={membersLoading || sharesLoading}
          pendingUserId={ctrl.pendingUserId}
          visibilityPending={ctrl.visibilityPending}
          saveStatus={ctrl.saveStatus}
          onDone={() => onOpenChange(false)}
        />
      </PopoverContent>
    </Popover>
  );
}

export default ShareConversationPopover;
